import { LoadingOverlayService } from '../ui/loading-overlay-service';
import { NativeValidationService } from '../validation/native-validation-service';

export interface DiscountResponse {
    success: boolean;
    message: string;
}

// IHPBA region name that requires Sub Region to be selected on Personal Details.
const AMERICAS_REGION_NAME = 'americas';

export class FormService {
    private loadingService = LoadingOverlayService.getInstance();
    private validationService = NativeValidationService.getInstance();

    // Helper methods for modern DOM manipulation
    private show(element: HTMLElement): void {
        element.style.display = '';
    }

    private hide(element: HTMLElement): void {
        element.style.display = 'none';
    }

    private setFieldValue(element: HTMLInputElement | HTMLSelectElement, value: string): void {
        const choices = (element as any)._choices;
        if (choices) {
            choices.setChoiceByValue(value);
        }
        element.value = value;
        element.dispatchEvent(new Event('change', { bubbles: true }));
    }

    private hasClass(element: HTMLElement, className: string): boolean {
        return element.classList.contains(className);
    }

    private addClass(element: HTMLElement, className: string): void {
        element.classList.add(className);
    }

    private removeClass(element: HTMLElement, className: string): void {
        element.classList.remove(className);
    }

    private closest(element: HTMLElement, selector: string): HTMLElement | null {
        return element.closest(selector) as HTMLElement;
    }

    private prop(element: HTMLInputElement | HTMLButtonElement, property: string, value: boolean): void {
        (element as any)[property] = value;
    }

    private data(element: HTMLElement, key: string, value?: any): any {
        if (value !== undefined) {
            element.dataset[key] = value;
            return value;
        }
        return element.dataset[key];
    }

    public initialize(): void {
        this.initializeForms();
        this.initializePageHandlers();
        this.initializeGoBackButton();
    }

    private initializeForms(): void {
        this.handleUsualFormReturn = this.handleUsualFormReturn.bind(this);
        this.setupSearchTermHandlers();
        this.setupAjaxForms();
        this.setupSubmitOnChangeElements();
        this.setupDiscountCodeHandlers();
    }

    private setupSearchTermHandlers(): void {
        const searchTermListeners = document.querySelectorAll('.js-form-listen-searchterm');
        
        // Event delegation for search term announcement forms
        document.addEventListener('submit', (e) => {
            const target = e.target as HTMLElement;
            if (target.matches('.js-form-announce-searchterm')) {
                e.preventDefault();
                const searchTermInput = target.querySelector('#pagination-search-box') as HTMLInputElement;
                if (searchTermInput) {
                    const searchTerm = searchTermInput.value;
                    searchTermListeners.forEach((listener) => {
                        listener.innerHTML = searchTerm;
                    });
                    const url = new URL(window.location.href);
                    url.searchParams.set('q', searchTerm);
                    window.history.pushState({}, '', url);
                }
            }
        });
    }

    private setupAjaxForms(): void {
        const ajaxForms = document.querySelectorAll("form.js-ajax") as NodeListOf<HTMLFormElement>;
        
        ajaxForms.forEach((form) => {
            if (!this.data(form, "hasAjaxSubmitEvent")) {
                form.addEventListener("submit", (e) => {
                    e.preventDefault();

                    const doBlockUI = this.hasClass(form, "js-ajax-blockui");

                    if (doBlockUI) {
                        this.loadingService.blockUI({
                            message: 'Please wait...'
                        });
                    }

                    if (this.hasClass(form, "js-page-reset-form")) {
                        this.resetFormPage(form);
                    }

                    this.performAjax(form,
                        (data: any) => {
                            if (data.result === "redirect") {
                                window.location.href = data.url;
                            } else {
                                const replaceOuter = form.dataset.ajaxReplaceOuter;
                                const replace = form.dataset.ajaxReplace;
                                
                                if (replaceOuter) {
                                    const targetElement = document.querySelector(replaceOuter);
                                    if (targetElement) {
                                        targetElement.outerHTML = data;
                                    }
                                } else if (replace) {
                                    const targetElement = document.querySelector(replace);
                                    if (targetElement) {
                                        targetElement.innerHTML = data;
                                    }
                                } else {
                                    if (form.parentElement) {
                                        form.parentElement.innerHTML = data;
                                    }
                                }

                                this.initialize(); // Re-initialize forms after content replacement
                                this.handleUsualFormReturn(form, doBlockUI);
                            }
                        },
                        (_error: any) => {
                            this.handleUsualFormReturn(form, doBlockUI);
                        });
                });

                this.data(form, "hasAjaxSubmitEvent", "true");

                if (this.hasClass(form, "js-submit-on-load")) {
                    form.dispatchEvent(new Event('submit'));
                }
            }
        });
    }

    private setupSubmitOnChangeElements(): void {
        const submitOnChangeElements = document.querySelectorAll(".js-submit-on-change");

        submitOnChangeElements.forEach((element) => {
            if (!this.data(element as HTMLElement, "hasSubmitOnChangeEvent")) {
                element.addEventListener("change", (e) => {
                    const form = this.closest(e.currentTarget as HTMLElement, "form");
                    if (form) {
                        form.dispatchEvent(new Event('submit'));
                    }
                });

                this.data(element as HTMLElement, "hasSubmitOnChangeEvent", "true");
            }
        });
    }

    private setupDiscountCodeHandlers(): void {
        const shopApplyDiscountUrl = "/umbraco/surface/CheckoutSurface/ApplyDiscount";
        const eventApplyDiscountUrl = "/umbraco/surface/EventBookingSurface/ApplyDiscount?id=";

        // Event delegation for discount application buttons
        document.addEventListener("click", (e) => {
            const target = e.target as HTMLElement;
            if (target.matches("a.js-apply-discount, button.js-apply-discount")) {
                this.applyDiscount(target, shopApplyDiscountUrl);
                e.preventDefault();
            }
        });

        // Event delegation for discount input keydown
        document.addEventListener("keydown", (e) => {
            const target = e.target as HTMLElement;
            if (target.matches("input.js-apply-discount") && (e as KeyboardEvent).keyCode === 13) {
                this.applyDiscount(target, shopApplyDiscountUrl);
                e.preventDefault();
                return false;
            }
        });

        // Event delegation for event discount application buttons
        document.addEventListener("click", (e) => {
            const target = e.target as HTMLElement;
            if (target.matches("a.js-apply-discount-event, button.js-apply-discount-event")) {
                const eventIdInput = document.querySelector("input[name=EventId]") as HTMLInputElement;
                const invoiceIdInput = document.querySelector("input[name=InvoiceId]") as HTMLInputElement;
                const eventId = eventIdInput?.value || "";
                const invoiceId = invoiceIdInput?.value || "";
                this.applyDiscount(target, eventApplyDiscountUrl + eventId + invoiceId);
                e.preventDefault();
            }
        });

        // Event delegation for event discount input keydown
        document.addEventListener("keydown", (e) => {
            const target = e.target as HTMLElement;
            if (target.matches("input.js-apply-discount-event") && (e as KeyboardEvent).keyCode === 13) {
                const eventIdInput = document.querySelector("input[name=EventId]") as HTMLInputElement;
                const invoiceIdInput = document.querySelector("input[name=InvoiceId]") as HTMLInputElement;
                const eventId = eventIdInput?.value || "";
                const invoiceId = invoiceIdInput?.value || "";
                this.applyDiscount(target, eventApplyDiscountUrl + eventId + invoiceId);
                e.preventDefault();
                return false;
            }
        });
    }

    private applyDiscount(triggerEl: HTMLElement, url: string): void {
        const container = this.closest(triggerEl, ".discount-code-container");
        if (!container) return;
        
        const messageEl = container.parentElement?.querySelector(".discount-code-message") as HTMLElement;
        const codeInput = container.querySelector("input[name=DiscountCode]") as HTMLInputElement;

        if (!codeInput || !messageEl) return;

        this.prop(triggerEl as HTMLButtonElement, "disabled", true);

        this.addClass(messageEl, "d-none");
        messageEl.textContent = "";

        const formData = new FormData();
        formData.append('code', codeInput.value);

        fetch(url, {
            method: "POST",
            body: formData,
            cache: 'no-cache'
        })
        .then(response => response.json())
        .then((data: DiscountResponse) => {
            if (data.success) {
                location.reload();
                data.message += " Refreshing...";
            } else {
                this.prop(triggerEl as HTMLButtonElement, "disabled", false);
            }

            this.removeClass(messageEl, "d-none");
            messageEl.textContent = data.message;
        })
        .catch((_error) => {
            this.prop(triggerEl as HTMLButtonElement, "disabled", false);
            this.removeClass(messageEl, "d-none");
            messageEl.textContent = "Something went wrong, please try again.";
        });
    }

    private initializePageHandlers(): void {
        // Event delegation for page number triggers
        document.addEventListener("click", (e) => {
            const target = e.target as HTMLElement;
            if (target.matches(".js-page-number-trigger")) {
                e.preventDefault();
                this.changePage(target);
            }
        });

        // Event delegation for page reset forms
        document.addEventListener("submit", (e) => {
            const target = e.target as HTMLElement;
            if (target.matches("form.js-page-reset-form")) {
                const eventData = (e as any).data;
                if (eventData && typeof eventData === "object" && eventData.pageload) {
                    return;
                }
                this.resetFormPage(target as HTMLFormElement);
            }
        });

        // Event delegation for form button locking
        document.addEventListener("submit", (e) => {
            const target = e.target as HTMLElement;
            if (target.matches("form.js-lock-buttons-on-submit")) {
                const buttons = target.querySelectorAll("a.btn, button, input[type=submit]") as NodeListOf<HTMLButtonElement | HTMLInputElement>;
                buttons.forEach(button => this.prop(button, "disabled", true));
            }
        });

        // Event delegation for trigger other elements
        document.addEventListener("click", (e) => {
            const target = e.target as HTMLElement;
            if (target.matches(".js-trigger-other")) {
                e.preventDefault();
                const fieldId = target.dataset.fieldid;
                if (fieldId) {
                    const targetElement = document.getElementById(fieldId) as HTMLElement;
                    if (targetElement) {
                        targetElement.click();
                    }
                }
            }
        });

        // Event delegation for go back triggers
        document.addEventListener("click", (e) => {
            const target = e.target as HTMLElement;
            if (target.matches(".js-goback-trigger")) {
                const form = this.closest(target, "form") as HTMLFormElement;
                if (!form) return;

                const fieldName = target.dataset.field || "GoBack";
                const hiddenField = form.querySelector(`input[name='${fieldName}']`) as HTMLInputElement;
                if (hiddenField) {
                    hiddenField.value = "true";
                }

                const validator = (form as any).validator;
                if (validator && validator.settings) {
                    validator.settings.ignore = "*";
                }

                form.submit();
            }
        });

        // Event delegation for file input changes
        document.addEventListener("change", (e) => {
            const target = e.target as HTMLInputElement;
            if (target.matches(".form__file input[type=file]")) {
                const files = target.files;
                const labelElement = target.parentElement?.querySelector(".form__file--label") as HTMLElement;

                if (files && files.length > 0 && labelElement) {
                    let text = "";
                    for (let i = 0; i < files.length; i++) {
                        text += files[i].name + (i !== files.length - 1 ? ", " : "");
                    }
                    labelElement.textContent = text;
                } else if (labelElement) {
                    labelElement.textContent = labelElement.dataset.default || "";
                }
            }
        });

        // Search icon click handler
        const searchIcon = document.getElementById('search-icon');
        if (searchIcon) {
            searchIcon.addEventListener("click", () => {
                const searchTerm = document.getElementById('SearchTerm') as HTMLInputElement;
                if (searchTerm) {
                    searchTerm.focus();
                }
            });
        }

        // Event delegation for copy value elements
        document.addEventListener("change", (e) => {
            const target = e.target as HTMLInputElement;
            if (target.matches(".js-copy-value")) {
                const form = this.closest(target, "form");
                if (!form) return;

                const fieldName = target.dataset.copytofield;
                if (fieldName) {
                    const targetField = form.querySelector(`input[name='${fieldName}']`) as HTMLInputElement;
                    if (targetField) {
                        this.setFieldValue(targetField, target.value);
                    }
                }
            }
        });

        this.setupPaymentTypeHandlers();
        this.setupBillingAddressHandlers();
        this.setupRegionSubRegionHandlers();
        this.setupTraineeTrainingHandlers();
        this.setupNationalChapterHandlers();
    }

    /**
     * Shared by every "enabled/required only when another field has a particular answer" dropdown on
     * Personal Details (SubRegion + Practice Setting when Region is "Americas"; National Chapter when
     * Member of National Chapter is "Yes"). [Required] is unconditional in the model for all of them, so
     * aspnet-client-validation's required check is already wired up from its one-time bootstrap scan of
     * data-val-required (present in the server-rendered HTML regardless of the controlling field). At
     * validate time it re-checks input.disabled live, so toggling `disabled` here is what actually turns
     * the check on/off.
     *
     * aria-required (not the native `required` attribute) is set below to keep it in sync for
     * accessibility/DOM semantics. Native `required` was tried here previously and had to be removed: it
     * makes the browser's own constraint validation intercept the submit before the 'submit' event ever
     * reaches aspnet-client-validation, so the field's styled data-valmsg-for message never rendered —
     * only a native, easy-to-miss browser tooltip did (see AccountCreation/Default.cshtml for the fuller
     * explanation of the same failure mode with a server-rendered `required`).
     */
    private applyConditionalSelectState(select: HTMLSelectElement, label: HTMLElement | null, enabled: boolean): void {
        const choices = (select as any)._choices;

        if (enabled) {
            select.disabled = false;
            if (choices) {
                choices.enable();
            }
            select.setAttribute('aria-required', 'true');
            if (label) {
                this.addClass(label, 'required');
            }
        } else {
            select.disabled = true;
            if (choices) {
                choices.disable();
            }
            select.setAttribute('aria-required', 'false');
            if (label) {
                this.removeClass(label, 'required');
            }
            this.setFieldValue(select, '');
            this.removeClass(select, 'input-validation-error');

            // Scoped to the field's own form, not the whole document — see the identically-reasoned
            // scoping in setupTraineeTrainingHandlers()'s clearValidation() below.
            const validationSpan = select.form?.querySelector(`[data-valmsg-for="${select.name}"]`) as HTMLElement;
            if (validationSpan) {
                validationSpan.textContent = '';
                this.removeClass(validationSpan, 'field-validation-error');
                this.addClass(validationSpan, 'field-validation-valid');
            }
        }
    }

    private setupRegionSubRegionHandlers(): void {
        const regionSelect = document.getElementById('Region') as HTMLSelectElement | null;
        const subRegionSelect = document.getElementById('SubRegion') as HTMLSelectElement | null;
        const practiceSettingSelect = document.getElementById('PracticeSetting') as HTMLSelectElement | null;

        if (!regionSelect || !subRegionSelect || this.data(regionSelect, 'hasRegionChangeEvent')) {
            return;
        }

        const subRegionLabel = document.querySelector('label[for="SubRegion"]') as HTMLElement | null;
        const practiceSettingLabel = document.querySelector('label[for="PracticeSetting"]') as HTMLElement | null;

        const applySubRegionState = (): void => {
            const selectedOption = regionSelect.options[regionSelect.selectedIndex];
            const isAmericas = !!selectedOption && selectedOption.text.trim().toLowerCase() === AMERICAS_REGION_NAME;

            this.applyConditionalSelectState(subRegionSelect, subRegionLabel, isAmericas);

            if (practiceSettingSelect) {
                this.applyConditionalSelectState(practiceSettingSelect, practiceSettingLabel, isAmericas);
            }
        };

        regionSelect.addEventListener('change', applySubRegionState);
        this.data(regionSelect, 'hasRegionChangeEvent', 'true');

        // Defer the initial state until after initChoices() has wrapped both selects (it runs
        // synchronously later in the same initializeServices() call). Applying our disabled state
        // to the raw <select> before Choices.js wraps it triggers a Choices init-time quirk where
        // it adds then immediately strips its own event listeners, leaving the dropdown unclickable
        // even after a later choices.enable() call.
        window.setTimeout(applySubRegionState, 0);
    }

    /**
     * National Chapter is only enabled once the applicant answers "Yes" to Member of National Chapter.
     * Follows the same disable/clear/re-enable shape as setupRegionSubRegionHandlers above.
     */
    private setupNationalChapterHandlers(): void {
        const memberOfChapterSelect = document.getElementById('IsMemberOfNationalChapter') as HTMLSelectElement | null;
        const chapterSelect = document.getElementById('ChapterId') as HTMLSelectElement | null;

        if (!memberOfChapterSelect || !chapterSelect || this.data(memberOfChapterSelect, 'hasNationalChapterChangeEvent')) {
            return;
        }

        const chapterLabel = document.querySelector('label[for="ChapterId"]') as HTMLElement | null;

        const applyChapterState = (): void => {
            const isMember = memberOfChapterSelect.value === 'true';
            this.applyConditionalSelectState(chapterSelect, chapterLabel, isMember);
        };

        memberOfChapterSelect.addEventListener('change', applyChapterState);
        this.data(memberOfChapterSelect, 'hasNationalChapterChangeEvent', 'true');

        // Defer until after initChoices() has wrapped the select — same reasoning as
        // setupRegionSubRegionHandlers above.
        window.setTimeout(applyChapterState, 0);
    }

    /**
     * Reveals the training-programme questions (training type, institution, expected end year) on
     * Personal Details when the selected Trainee Status is the "currently in training" one, identified
     * by the option value the server renders in data-currently-in-training-value. Year Completed
     * Training is hidden for all Junior applications server-side (fixed for the page's lifetime — it's
     * not reactive to Trainee Status), so this only needs to manage the questions container and its
     * own fields.
     */
    private setupTraineeTrainingHandlers(): void {
        const traineeStatusSelect = document.getElementById('TraineeStatus') as HTMLSelectElement | null;
        const questionsContainer = document.getElementById('traineeTrainingQuestions') as HTMLElement | null;

        if (!traineeStatusSelect || !questionsContainer || this.data(traineeStatusSelect, 'hasTraineeStatusChangeEvent')) {
            return;
        }

        const isCurrentlyInTrainingInput = document.getElementById('IsCurrentlyInTraining') as HTMLInputElement | null;
        const yearSelect = questionsContainer.querySelector('select[name="YearCompletedTraining"]') as HTMLSelectElement | null;
        const trainingTypeSelect = document.getElementById('TrainingType') as HTMLSelectElement | null;
        const trainingInstitutionInput = document.getElementById('TrainingInstitution') as HTMLInputElement | null;
        const trainingTypeOtherField = document.getElementById('trainingTypeOtherField') as HTMLElement | null;
        const trainingTypeOtherInput = document.getElementById('TrainingTypeOther') as HTMLInputElement | null;

        // Sets aria-required rather than the native `required` attribute deliberately: native `required`
        // makes the browser's own constraint validation intercept the submit before the 'submit' event
        // ever reaches aspnet-client-validation, so this field's styled data-valmsg-for message never
        // renders — only a native, easy-to-miss browser tooltip does (see AccountCreation/Default.cshtml
        // for the fuller explanation of the same failure mode with a server-rendered `required`). This
        // field's `data-val-required` already exists unconditionally from [Required] on the model, and
        // `disabled` toggling above already turns aspnet-client-validation's check on/off correctly —
        // aria-required only adds the accessibility signal for assistive tech without re-introducing the
        // native short-circuit.
        const setRequired = (el: HTMLElement | null, required: boolean): void => {
            if (!el) return;
            el.setAttribute('aria-required', required ? 'true' : 'false');
        };

        const clearValidation = (el: (HTMLInputElement | HTMLSelectElement) | null): void => {
            if (!el) return;
            this.removeClass(el, 'input-validation-error');
            // Scoped to the field's own wrapping column rather than a global lookup: YearCompletedTraining
            // is bound by both the (hidden) free-text field and this dropdown, so a document-wide
            // `[data-valmsg-for="YearCompletedTraining"]` query would always resolve to whichever one comes
            // first in the DOM, clearing the wrong field's message. `closest` also survives Choices.js
            // wrapping the <select> in its own inner container, since it walks up past that wrapper too.
            const validationSpan = el.closest('.col-12')?.querySelector(`[data-valmsg-for="${el.name}"]`) as HTMLElement;
            if (validationSpan) {
                validationSpan.textContent = '';
                this.removeClass(validationSpan, 'field-validation-error');
                this.addClass(validationSpan, 'field-validation-valid');
            }
        };

        const setEnabled = (el: (HTMLInputElement | HTMLSelectElement) | null, enabled: boolean): void => {
            if (!el) return;
            el.disabled = !enabled;
            setRequired(el, enabled);
            const choices = (el as any)._choices;
            if (choices) {
                enabled ? choices.enable() : choices.disable();
            }
            if (!enabled) {
                this.setFieldValue(el, '');
                clearValidation(el);
            }
        };

        const applyTrainingTypeOtherState = (): void => {
            const otherValue = trainingTypeSelect?.dataset.otherValue;
            const isOther = !!trainingTypeSelect && !trainingTypeSelect.disabled && !!otherValue && trainingTypeSelect.value === otherValue;

            if (trainingTypeOtherField) {
                isOther ? this.show(trainingTypeOtherField) : this.hide(trainingTypeOtherField);
            }
            setEnabled(trainingTypeOtherInput, isOther);
        };

        // The D365 option value that means "currently in training", resolved server-side from
        // TraineeStatusNames.CurrentlyInTraining and rendered onto the select. Comparing values keeps the
        // option label in one place (the C# constant) rather than duplicating it here, where a label edit
        // in D365 would silently stop matching. Absent if the server could not resolve it — in which case
        // treat nothing as "in training", matching what the controller does with IsCurrentlyInTraining.
        const currentlyInTrainingValue = traineeStatusSelect.dataset.currentlyInTrainingValue;

        const applyTraineeTrainingState = (): void => {
            const isCurrentlyInTraining = !!currentlyInTrainingValue && traineeStatusSelect.value === currentlyInTrainingValue;

            // Keep the posted hidden flag in sync — the controller trusts this value (rather than
            // re-resolving Trainee Status server-side) to decide whether to keep or null out
            // TrainingType/TrainingTypeOther/TrainingInstitution on submit.
            if (isCurrentlyInTrainingInput) {
                isCurrentlyInTrainingInput.value = isCurrentlyInTraining ? 'true' : 'false';
            }

            isCurrentlyInTraining ? this.show(questionsContainer) : this.hide(questionsContainer);

            setEnabled(trainingTypeSelect, isCurrentlyInTraining);
            setEnabled(trainingInstitutionInput, isCurrentlyInTraining);
            setEnabled(yearSelect, isCurrentlyInTraining);

            applyTrainingTypeOtherState();
        };

        traineeStatusSelect.addEventListener('change', applyTraineeTrainingState);
        this.data(traineeStatusSelect, 'hasTraineeStatusChangeEvent', 'true');

        if (trainingTypeSelect) {
            trainingTypeSelect.addEventListener('change', applyTrainingTypeOtherState);
        }

        // Defer until after initChoices() has wrapped the selects — same reasoning as
        // setupRegionSubRegionHandlers above.
        window.setTimeout(applyTraineeTrainingState, 0);
    }

    private setupPaymentTypeHandlers(): void {
        const payTypeElements = document.querySelectorAll(".js-pay-type");
        
        payTypeElements.forEach(element => {
            element.addEventListener("click", (e) => {
                e.preventDefault();

                const target = e.currentTarget as HTMLElement;
                const id = target.dataset.id;

                if (id === "card") {
                    document.querySelectorAll('.js-card').forEach(el => this.show(el as HTMLElement));
                    document.querySelectorAll('.js-invoice').forEach(el => this.hide(el as HTMLElement));
                    document.querySelectorAll('.js-directDebit').forEach(el => this.hide(el as HTMLElement));
                    document.querySelectorAll('.js-pay-type').forEach(el => this.removeClass(el as HTMLElement, 'active'));
                    this.addClass(target, 'active');
                    
                    this.setRequiredField("#tAndc", false);
                    this.setRequiredField('#cardholder-name', true);
                    this.setRequiredField('#cardNumber', true);
                    this.setRequiredField('#cardExpiry', true);
                    this.setRequiredField('#cardCVC', true);
                    this.setRequiredField('#flexCheckDefault', true);
                    this.setRequiredField('#DirectDebitSelectFrequency', false);
                    
                    this.setFieldValue(document.querySelector('#PayByInvoice') as HTMLInputElement, 'false');
                    this.setFieldValue(document.querySelector('#PayByCard') as HTMLInputElement, 'true');
                    this.setFieldValue(document.querySelector('#PayByDirectDebit') as HTMLInputElement, 'false');
                }
                else if (id === "invoice") {
                    document.querySelectorAll('.js-card').forEach(el => this.hide(el as HTMLElement));
                    document.querySelectorAll('.js-invoice').forEach(el => this.show(el as HTMLElement));
                    document.querySelectorAll('.js-directDebit').forEach(el => this.hide(el as HTMLElement));
                    document.querySelectorAll('.js-pay-type').forEach(el => this.removeClass(el as HTMLElement, 'active'));
                    this.addClass(target, 'active');
                    
                    this.setRequiredField("#tAndc", true);
                    this.setRequiredField('#cardholder-name', false);
                    this.setRequiredField('#cardNumber', false);
                    this.setRequiredField('#cardExpiry', false);
                    this.setRequiredField('#cardCVC', false);
                    this.setRequiredField('#flexCheckDefault', false);
                    this.setRequiredField('#DirectDebitSelectFrequency', false);
                    
                    this.setFieldValue(document.querySelector('#PayByInvoice') as HTMLInputElement, 'true');
                    this.setFieldValue(document.querySelector('#PayByCard') as HTMLInputElement, 'false');
                    this.setFieldValue(document.querySelector('#PayByDirectDebit') as HTMLInputElement, 'false');
                }
                else if (id === "directDebit") {
                    document.querySelectorAll('.js-card').forEach(el => this.hide(el as HTMLElement));
                    document.querySelectorAll('.js-invoice').forEach(el => this.hide(el as HTMLElement));
                    document.querySelectorAll('.js-directDebit').forEach(el => this.show(el as HTMLElement));
                    document.querySelectorAll('.js-pay-type').forEach(el => this.removeClass(el as HTMLElement, 'active'));
                    this.addClass(target, 'active');
                    
                    this.setRequiredField("#tAndc", true);
                    this.setRequiredField('#cardholder-name', false);
                    this.setRequiredField('#cardNumber', false);
                    this.setRequiredField('#cardExpiry', false);
                    this.setRequiredField('#cardCVC', false);
                    this.setRequiredField('#flexCheckDefault', false);
                    this.setRequiredField('#DirectDebitSelectFrequency', true);
                    
                    this.setFieldValue(document.querySelector('#PayByInvoice') as HTMLInputElement, 'false');
                    this.setFieldValue(document.querySelector('#PayByCard') as HTMLInputElement, 'false');
                    this.setFieldValue(document.querySelector('#PayByDirectDebit') as HTMLInputElement, 'true');
                }
            });
        });
    }

    private setRequiredField(selector: string, required: boolean): void {
        const field = document.querySelector(selector) as HTMLInputElement;
        if (field) {
            if (required) {
                field.setAttribute('required', 'required');
            } else {
                field.removeAttribute('required');
            }
        }
    }

    private setupBillingAddressHandlers(): void {
        const sameAsAboveInputs = document.querySelectorAll('input[name="SameAsAbove"]');

        sameAsAboveInputs.forEach(input => {
            input.addEventListener('change', (e) => {
                const target = e.currentTarget as HTMLInputElement;
                const hiddenField = document.querySelector('input[type="checkbox"][name="SameAsAbove"]') as HTMLInputElement;
                const checkoutForm = this.closest(target, 'form');

                if (!checkoutForm || !hiddenField) return;
                
                const billingForm = checkoutForm.querySelector("#billingDetails") as HTMLElement;
                const billingFields = billingForm?.querySelectorAll("input, select") as NodeListOf<HTMLInputElement | HTMLSelectElement>;
                const requiredBillingFields = Array.from(billingFields || []).filter(field => 
                    field.hasAttribute("data-val-required")
                );

                if (target.checked) {
                    hiddenField.value = "true";
                    if (billingForm) this.hide(billingForm);
                    
                    requiredBillingFields.forEach(field => {
                        delete (field as any).dataset.val;
                    });
                } else {
                    hiddenField.value = "false";
                    if (billingForm) this.show(billingForm);
                    
                    requiredBillingFields.forEach(field => {
                        (field as any).dataset.val = "true";
                    });
                }

                // Remove existing validation and re-parse
                const form = checkoutForm as any;
                delete form.dataset.validator;
                delete form.dataset.unobtrusiveValidation;
                
                // Re-initialize validation with native service
                this.validationService.parse(checkoutForm as HTMLFormElement);
                
                // Fallback to jQuery validator if available for compatibility
                if ((window as any).$ && (window as any).$.validator) {
                    (window as any).$.validator.unobtrusive.parse(checkoutForm);
                }
            });
        });
    }

    private initializeGoBackButton(): void {
        let formGoBackBtn = document.querySelector('.js-go-back');
        if (formGoBackBtn) {
            formGoBackBtn.addEventListener('click', (e) => {
                e.preventDefault();

                var form = formGoBackBtn.closest('form');
                var goBackHiddenField = form?.querySelector('input[id="GoBack"]') as HTMLInputElement;

                if (form && goBackHiddenField) {
                    goBackHiddenField.value = "True";
                    goBackHiddenField.dispatchEvent(new Event('change'));
                    (form as HTMLFormElement).submit();
                }
            });
        }
    }

    private handleUsualFormReturn(form: HTMLFormElement, doBlockUI: boolean): void {
        if (doBlockUI) {
            this.loadingService.unblockUI();
        }

        const importantContainer = form.querySelector(".error-container, .success-container") as HTMLElement;

        if (importantContainer) {
            if (window.App && window.App.smoothScrollTo) {
                window.App.smoothScrollTo(importantContainer);
            }
        } else {
            // Only scroll to the form automatically if this isn't an onload submission
            const isFirstLoadSubmission = this.hasClass(form, "js-submit-on-load") && !this.data(form, "hasSubmittedBefore");

            if (!isFirstLoadSubmission) {
                if (window.App && window.App.smoothScrollTo) {
                    window.App.smoothScrollTo(form, false, 250);
                }
            }
        }

        this.data(form, "hasSubmittedBefore", "true");
    }

    private async performAjax(form: HTMLFormElement, successFunc: Function, errorFunc: Function): Promise<void> {
        try {
            const method = form.getAttribute("method") || "POST";
            const url = form.getAttribute("action") || "";
            
            let body: FormData | URLSearchParams;
            let headers: HeadersInit = {};

            if (this.hasClass(form, "js-formdata")) {
                body = new FormData(form);
            } else {
                body = new URLSearchParams(new FormData(form) as any);
                headers['Content-Type'] = 'application/x-www-form-urlencoded';
            }

            const response = await fetch(url, {
                method: method,
                body: body,
                headers: headers,
                cache: 'no-cache'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const contentType = response.headers.get('content-type');
            let data;
            
            if (contentType && contentType.includes('application/json')) {
                data = await response.json();
            } else {
                data = await response.text();
            }

            successFunc(data);
        } catch (error) {
            errorFunc(error);
        }
    }

    private resetFormPage(form: HTMLFormElement): void {
        const activeElement = document.activeElement as HTMLElement;

        // If there is an active element, and it is within this form, and it isn't a pagination button, then reset the page
        if (activeElement && form.contains(activeElement) && 
            !this.hasClass(activeElement, "js-page-number-trigger") && 
            !this.hasClass(activeElement, "page-link")) {
            
            const pageVar = form.dataset.targetField;
            const pageName = pageVar || "Pagination.CurrentPage";

            const pageField = form.querySelector(`[name='${pageName}']`) as HTMLInputElement;
            if (pageField) {
                this.setFieldValue(pageField, "1");
            }
        }
    }

    private changePage(element: HTMLElement, pageNum?: number): void {
        const page = pageNum !== undefined ? pageNum : element.dataset.targetPage;

        if (page !== "" && page !== undefined) {
            const isSafari = navigator.vendor && navigator.vendor.indexOf("Apple") > -1 &&
                navigator.userAgent &&
                navigator.userAgent.indexOf("CriOS") === -1 &&
                navigator.userAgent.indexOf("FxiOS") === -1;

            const pageVar = element.dataset.targetField;
            const form = this.closest(element, "form") as HTMLFormElement;
            if (!form) return;
            
            const pageName = pageVar || "Pagination.CurrentPage";

            const varElement = form.querySelector(`[name='${pageName}']`) as HTMLInputElement;
            if (varElement) {
                this.setFieldValue(varElement, String(page));

                if (isSafari) {
                    // Actually disgusting that I have to do this for Safari
                    const replaceVarElement = document.createElement("input");
                    replaceVarElement.id = varElement.id + "S";
                    replaceVarElement.name = (pageVar && pageVar.replace(/"/g, "") || varElement.name) + "S";
                    replaceVarElement.type = "hidden";
                    replaceVarElement.value = String(page);

                    varElement.parentElement?.appendChild(replaceVarElement);
                }

                form.submit();
            }
        }
    }
}

// Global window extension for compatibility
declare global {
    interface Window {
        App?: any;
    }
}
